import PageHead from "../components/PageHead";
import CTA from "../components/CTA";

const SECTORS = [
  ["Sector 01", "Logistics & freight",
    "Shipping runs on paperwork, and most of it arrives mixed together. Docket splits the packet, classifies each document, and turns a daily flood of shipping paper into structured, trackable records.",
    ["Bills of lading", "Packing lists", "Customs declarations", "Proof of delivery", "Freight invoices"],
    "A freight forwarder turns each carrier's mixed PDF batch into separated, classified records ready for the TMS, without anyone pre-sorting pages."],
  ["Sector 02", "Finance & accounting",
    "Accounts-payable work is high-volume, repetitive and unforgiving of error. Docket extracts header fields and line items with a confidence score on each, so clean documents clear automatically and only genuine exceptions reach a person.",
    ["Invoices", "Purchase orders", "Bank statements", "Remittance advice", "Expense receipts"],
    "An accounts-payable team clears the daily invoice queue with keystrokes spent only on the small share of documents that are actually flagged."],
  ["Sector 03", "Legal & compliance",
    "Legal documents are long, dense and consequential. Docket extracts the terms that matter and generates a plain-language summary of each document, so review teams get to the substance faster, with an audit trail behind every step.",
    ["Contracts", "NDAs", "Court filings", "Case dockets", "Correspondence"],
    "A legal-operations team pulls key dates, parties and obligations from an incoming contract set, with a summary attached to each file."],
  ["Sector 04", "Healthcare & insurance",
    "Claims and intake processing combines high volume with sensitive personal data. Docket keeps documents inside an isolated tenant, routes only low-confidence fields to reviewers, and can anonymise personal data when audit information is exported.",
    ["Claims forms", "Intake forms", "Referral letters", "Lab reports", "Insurance cards"],
    "A claims unit processes intake forms at volume, with PII anonymised on export and every action captured in the audit log."],
  ["Sector 05", "Retail & supply chain",
    "Supplier paperwork arrives from hundreds of sources in hundreds of layouts. Because Docket is trained rather than templated, it handles that variety as a matter of course, and reconciles documents into clean, comparable data.",
    ["Purchase orders", "Supplier invoices", "Delivery notes", "Returns forms", "Price lists"],
    "A merchandising team reconciles supplier invoices against purchase orders without re-keying a single line from a PDF."],
];

export default function Industries() {
  return (
    <>
      <PageHead
        crumb="Industries"
        eyebrow="Industries"
        title="Industry-agnostic by design."
        lede={`Docket has no built-in list of supported documents, so there is no list to be left off. Because it learns from the examples you give it, the question is never "does it support my industry?" It is "can you show it your documents?"`}
      />

      <section className="section">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>Why agnostic matters</div>
              <h2>Most IDP tools ship a fixed menu. Docket ships a method.</h2>
              <p>Tools built around pre-set templates work beautifully, right up to the document they were never built for.</p>
              <p className="mark">Docket inverts that. The training interface is the product, so the document set is yours to define.</p>
            </div>
            <div className="plist">
              {[
                ["No template lock-in", "Your documents are not forced to resemble a vendor's idea of an invoice or a form. You train Docket on the real thing."],
                ["Niche documents are first-class", "An unusual internal form or a region-specific layout is trained exactly like any other document type, no special case, no exception."],
                ["One platform across departments", "Finance, operations and legal can each train their own models inside the same tenant, instead of buying three different tools."],
              ].map(([h, p], i) => (
                <div key={h} className={`pitem reveal${i ? ` d${i}` : ""}`}>
                  <div className="pitem__no">→</div>
                  <div><h3>{h}</h3><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section bg-2 bordered">
        <div className="container">
          <div className="s-head reveal" style={{ marginBottom: 20 }}>
            <div className="eyebrow">Where it fits</div>
            <h2>Five sectors, one platform, and these are only examples.</h2>
            <p className="lede">The document types below are illustrative, not a supported-list. They show the shape of the work Docket is built for.</p>
          </div>
          <div>
            {SECTORS.map(([no, name, body, docs, use]) => (
              <div key={name} className="sector reveal">
                <div>
                  <div className="sector__no">{no}</div>
                  <div className="sector__name">{name}</div>
                </div>
                <div className="sector__body">
                  <p>{body}</p>
                  <div className="sector__docs">{docs.map((t) => <span key={t} className="tag">{t}</span>)}</div>
                  <p className="sector__use">{use}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="s-head s-head--center reveal" style={{ marginBottom: 34 }}>
            <div className="eyebrow eyebrow--center">And beyond</div>
            <h2>If you can show it examples, it can learn to process it.</h2>
            <p className="lede">Your industry may not be one of the five above, and it does not need to be. The platform stays the same; only the training data changes.</p>
          </div>
          <div className="chips reveal">
            <div className="chip">
              <div className="chip__name">Manufacturing</div>
              <div className="chip__docs">Spec Sheets, Work Orders, Bills of Materials, Packing Slips, Supplier Certificates, QA Reports <span className="chip__more">+ more</span></div>
            </div>
            <div className="chip">
              <div className="chip__name">Real Estate</div>
              <div className="chip__docs">Leases, Title Deeds, Mortgage Agreements, Settlement Statements, Appraisals, Disclosures <span className="chip__more">+ more</span></div>
            </div>
            <div className="chip">
              <div className="chip__name">Public Sector</div>
              <div className="chip__docs">Applications, Permits, Tax Filings, Benefit Claims, Citizen Forms, Case and Court Files <span className="chip__more">+ more</span></div>
            </div>
            <div className="chip">
              <div className="chip__name">Energy &amp; Utilities</div>
              <div className="chip__docs">Meter Reports, Service Records, Supply Contracts, Safety Inspections, Regulatory Filings <span className="chip__more">+ more</span></div>
            </div>
            <div className="chip chip--any">
              <div className="chip__name">Your Documents</div>
              <div className="chip__docs">Whatever your team processes by hand today. Train a Model, Publish, Process at Scale.</div>
            </div>
          </div>
        </div>
      </section>

      <CTA
        title='Bring the documents <em>your</em> industry runs on.'
        text="A pilot trains Docket on two or three of your real document types, whatever they are, and shows you the accuracy and the automation gain on your own data."
      />
    </>
  );
}
